import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["allowHide", "children", "className", "contentType", "embedded", "flash", "focusCloseButton", "fwdRef", "icon", "iconLabel", "iconStyle", "inline", "onHide", "timeout", "title", "titleTag", "type", "wrapperTag"],
  _excluded2 = ["role"];
import React, { forwardRef, useState, useEffect, useCallback, useId } from 'react';
import SmoothCollapse from 'react-smooth-collapse';
import { buildVersionedClass, buildVersionedClassNames } from '@op/opds.opux-utilities';
import AlertIcon from './_alert-icon';
import AlertCloseButton from './_alert-close-button';
import StatusMessage from '../status-message/status-message';
function getAlertRole(type) {
  return type === 'critical' ? 'alert' : 'status';
}

/**
 * _Alerts provide informational messages to users._
 *
 * ***
 *
 * The component props allow controlling the type and style of the message,
 * whether it has a title, is closable, etc.
 *
 * Refer to the [Accessibility](#/Feedback%20%26%20Inform/Alert?id=setting-contenttype-property)
 * section to make a conscious decision on the `contentType` property value correct for your use case.
 *
 * Additional props (including `ref`) will be forwarded to the wrapping HTML element.
 */
export const Alert = _ref => {
  let {
      allowHide = false,
      children,
      className,
      contentType: propContentType = 'static',
      embedded = false,
      flash = false,
      focusCloseButton = false,
      fwdRef,
      icon,
      iconLabel,
      iconStyle,
      inline = false,
      onHide,
      timeout,
      title,
      titleTag,
      type = 'notification',
      wrapperTag: WrapperTag = 'section'
    } = _ref,
    props = _objectWithoutPropertiesLoose(_ref, _excluded);
  const [show, setShow] = useState(true);
  // Fix for buttonRef.current being undefined inside useEffect() https://stackoverflow.com/a/63033314
  const buttonRef = useCallback(button => {
    if (button !== null && focusCloseButton && (allowHide || flash) && show) {
      button.focus();
    }
  }, [allowHide, flash, focusCloseButton, show]);
  const cn = buildVersionedClassNames('ds-alert', className, {
    'ds-alert--embedded': embedded,
    'ds-alert--critical': type === 'critical' && !inline,
    'ds-alert--flash': flash,
    'ds-alert--information': type === 'information' && !inline,
    'ds-alert--inline': inline,
    'ds-alert--inline-large': inline === 'large',
    'ds-alert--positive': type === 'positive' && !inline,
    'ds-alert--visible': show && (flash || inline),
    'ds-alert--warning': type === 'warning' && !inline
  });
  const alertRole = getAlertRole(type);
  const svgIconID = "ds-a-" + useId();
  const titleID = "ds-a-" + useId();
  const combinedIDs = title ? svgIconID + " " + titleID : svgIconID;
  const TitleTag = (() => {
    if (titleTag) {
      return titleTag;
    }
    return embedded ? 'h3' : 'h2';
  })();
  const rest = _objectWithoutPropertiesLoose(props, _excluded2);
  const useSection = !(flash || inline);
  const contentType = flash ? 'dynamic' : propContentType;
  const propAllowHide = flash || inline ? false : allowHide;
  const propTitle = flash || inline ? undefined : title;
  const propTimeout = (() => {
    if ((flash || inline) && !timeout) {
      return flash ? 5000 : 0;
    }
    return timeout;
  })();
  function hide(event) {
    event == null || event.preventDefault();
    setShow(false);
  }
  function handleKeyDown(event) {
    if (event.key === ' ' || event.key === 'Enter' || event.key === 'Escape') {
      hide(event);
    }
  }

  // Mount/unmount
  useEffect(() => {
    if ((flash || inline) && propTimeout) {
      const hider = setTimeout(event => hide(event), propTimeout);
      return () => {
        clearTimeout(hider);
      };
    }
  });

  // Update
  useEffect(() => {
    if (onHide && !show) {
      onHide();
    }
  }, [show]);
  const content = /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: buildVersionedClass('ds-alert__icon')
  }, /*#__PURE__*/React.createElement(AlertIcon, {
    embedded: embedded,
    flash: flash,
    icon: icon,
    iconlabel: iconLabel,
    id: svgIconID,
    inline: inline,
    style: iconStyle,
    type: type
  })), /*#__PURE__*/React.createElement("div", {
    className: buildVersionedClass('ds-alert__body')
  }, propTitle && /*#__PURE__*/React.createElement(TitleTag, {
    className: buildVersionedClass('ds-alert__title'),
    id: titleID
  }, propTitle), children), (propAllowHide || flash) && /*#__PURE__*/React.createElement(AlertCloseButton, {
    buttonRef: buttonRef,
    flash: flash,
    handleKeyDown: handleKeyDown,
    hide: hide
  }));
  const alert = /*#__PURE__*/React.createElement(WrapperTag, _extends({
    "aria-labelledby": useSection ? combinedIDs : undefined,
    className: cn,
    ref: fwdRef,
    role: undefined
  }, rest), contentType === 'static' ? content : /*#__PURE__*/React.createElement(StatusMessage, {
    role: alertRole
  }, content));
  if (propAllowHide || flash || inline) {
    return /*#__PURE__*/React.createElement(SmoothCollapse, {
      allowOverflowWhenOpen: true,
      expanded: show
    }, alert);
  }
  return alert;
};
const Fwd = /*#__PURE__*/forwardRef((props, ref) => /*#__PURE__*/React.createElement(Alert, _extends({}, props, {
  fwdRef: ref
})));
Fwd.displayName = 'Alert';
export default Fwd;